import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
import { 
  Clock, 
  Euro, 
  Rocket, 
  Target, 
  CheckCircle, 
  Zap, 
  Users, 
  TrendingUp,
  ArrowRight,
  Star,
  Shield,
  Lightbulb
} from "lucide-react";

const KIMVPDevelopment = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const benefits = [
    {
      icon: <Target className="h-8 w-8 text-brand-400" />,
      title: t('mvp.benefits.testing.title'),
      description: t('mvp.benefits.testing.desc')
    },
    {
      icon: <Users className="h-8 w-8 text-brand-400" />,
      title: t('mvp.benefits.acceptance.title'),
      description: t('mvp.benefits.acceptance.desc')
    },
    {
      icon: <Clock className="h-8 w-8 text-brand-400" />,
      title: t('mvp.benefits.speed.title'),
      description: t('mvp.benefits.speed.desc')
    }
  ];

  const advantages = [
    {
      icon: <Rocket className="h-6 w-6 text-white" />,
      title: t('mvp.advantages.market.title'),
      description: t('mvp.advantages.market.desc')
    },
    {
      icon: <Zap className="h-6 w-6 text-white" />,
      title: t('mvp.advantages.flexibility.title'),
      description: t('mvp.advantages.flexibility.desc')
    },
    {
      icon: <Shield className="h-6 w-6 text-white" />,
      title: t('mvp.advantages.risk.title'),
      description: t('mvp.advantages.risk.desc')
    },
    {
      icon: <Lightbulb className="h-6 w-6 text-white" />,
      title: t('mvp.advantages.learning.title'),
      description: t('mvp.advantages.learning.desc')
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead 
        title={t('mvp.seo.title')}
        description={t('mvp.seo.description')}
        keywords={t('mvp.seo.keywords')}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-blue-500/10"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-[46rem]">
            <div className="inline-flex items-center px-4 py-2 bg-teal-600/20 rounded-full text-brand-400 text-sm font-medium mb-6">
              <Star className="h-4 w-4 mr-2" />
              {t('mvp.hero.badge')}
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t('mvp.hero.title')}
            </h1>
            
            <p className="text-xl text-ink-muted mb-8 max-w-[62ch] leading-relaxed">
              {t('mvp.hero.subtitle')}
            </p>
            
            {/* Price Highlight */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 mb-8 max-w-[62ch]">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <Euro className="h-8 w-8 text-white" />
                <span className="text-4xl font-bold text-white">19.000 EUR</span>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Clock className="h-6 w-6 text-brand-300" />
                <span className="text-lg text-brand-300">{t('mvp.hero.timeline')}</span>
              </div>
            </div>
            
            <button
              onClick={openContactModal}
              className="bg-brand hover:bg-brand-600 text-ink-onBrand font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 inline-flex items-center gap-2 text-lg"
            >
              {t('mvp.hero.cta')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-12 text-center">
              {t('mvp.whatYouGet.title')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-surface-sunken hover:bg-surface-raised transition-colors">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-brand/10 rounded-lg">
                      {benefit.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-ink mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-ink-muted leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('mvp.additional.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {advantages.map((advantage, index) => (
                <div key={index} className="bg-white/[0.07] backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 bg-brand rounded-lg">
                      {advantage.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {advantage.title}
                      </h3>
                      <p className="text-ink-muted leading-relaxed">
                        {advantage.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Best Part */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-8">
              {t('mvp.bestPart.title')}
            </h2>
            
            <div className="bg-gradient-to-r from-brand/10 to-blue-500/10 rounded-2xl p-8 mb-8">
              <p className="text-lg text-ink-muted leading-relaxed mb-6">
                {t('mvp.bestPart.description')}
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center space-x-2 bg-surface px-4 py-2 rounded-full">
                  <CheckCircle className="h-5 w-5 text-brand-400" />
                  <span className="text-ink-muted font-medium">{t('mvp.bestPart.benefit1')}</span>
                </div>
                <div className="flex items-center space-x-2 bg-surface px-4 py-2 rounded-full">
                  <CheckCircle className="h-5 w-5 text-brand-400" />
                  <span className="text-ink-muted font-medium">{t('mvp.bestPart.benefit2')}</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={openContactModal}
              className="bg-brand hover:bg-brand-600 text-ink-onBrand font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 inline-flex items-center gap-2 text-lg"
            >
              {t('mvp.bestPart.cta')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-MVP-Entwicklung"
        topic="KI-MVP-Entwicklung"
        buttonText={t('mvp.contact.defaultMessage')}
      />
    </div>
  );
};

export default KIMVPDevelopment;